import React, { useState } from 'react';
import styled from 'styled-components';
import { TextField } from '@toqio/toqio-web-components';
import { getAccountIdentifierField, type BankContext } from '../accountIdentifiers';

type Props = {
  /** The selected bank's `inputRequired` keys (SNAKE_CASE, e.g. `PSU_ID`). */
  keys: string[];
  /** Selected bank name + country — some fields interpolate/validate on them. */
  bank: BankContext;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
};

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/**
 * Renders one `TextField` per account-identifier key the selected bank
 * requires (SEB-1664). Each field's label / hint / placeholder / validation
 * come from the mapping in `accountIdentifiers.ts`. Validation errors surface
 * once a field has been blurred (touched); the parent gates "Continue" on
 * overall validity regardless of touched state.
 */
export const AccountIdentifierFields: React.FC<Props> = ({
  keys,
  bank,
  values,
  onChange,
  disabled,
}) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  return (
    <Fields>
      {keys.map((key) => {
        const field = getAccountIdentifierField(key, bank);
        const value = values[key] ?? '';
        const showError = touched[key] && !field.validate(value);
        const inputId = `external-bank-identifier-${key}`;
        return (
          <TextField
            key={key}
            id={inputId}
            htmlFor={inputId}
            label={field.label}
            hint={field.hint}
            placeholder={field.placeholder}
            value={value}
            disabled={disabled}
            error={showError ? field.error : undefined}
            ariaInvalid={showError}
            onChange={(next: string) => onChange(key, next)}
            onBlur={() => setTouched((prev) => ({ ...prev, [key]: true }))}
          />
        );
      })}
    </Fields>
  );
};
